import { createContext, useCallback, useContext, useMemo, useState } from "react";
import {
  IT_PORTAL_I18N,
  detectITPortalLocale,
  persistITPortalLocale,
} from "./itPortal";

/**
 * React context propagating the resolved IT Portal locale (``IT`` or
 * ``EN``) and the corresponding string dictionary ``t`` to every
 * descendant widget. Wrap the IT Portal page in ``<ITPortalI18nProvider>``
 * and consume via ``const { t, locale, setLocale } = useITPortalI18n();``.
 */
const ITPortalI18nContext = createContext(null);

export function ITPortalI18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => detectITPortalLocale());

  const setLocale = useCallback((next) => {
    if (next !== "IT" && next !== "EN") return;
    setLocaleState(next);
    persistITPortalLocale(next);
  }, []);

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t: IT_PORTAL_I18N[locale] || IT_PORTAL_I18N.EN,
    }),
    [locale, setLocale],
  );

  return (
    <ITPortalI18nContext.Provider value={value}>
      {children}
    </ITPortalI18nContext.Provider>
  );
}

export function useITPortalI18n() {
  const ctx = useContext(ITPortalI18nContext);
  if (!ctx) {
    // Allow widgets to be rendered outside the provider (e.g. when
    // CommunicationsTab is reused inside /admin/communications):
    // fall back to the auto-detected locale so we never crash.
    const fallback = detectITPortalLocale();
    return {
      locale: fallback,
      setLocale: () => undefined,
      t: IT_PORTAL_I18N[fallback] || IT_PORTAL_I18N.EN,
    };
  }
  return ctx;
}
